import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import * as Sentry from "@sentry/react";

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Functional component for the error UI to use the translation hook
 */
function ErrorFallback({ error, onReset }: { error: Error | null, onReset: () => void }) {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center animate-fade-in border border-slate-100">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>

                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('error.boundary_title')}
                </h1>

                <p className="text-slate-600 mb-6">
                    {t('error.boundary_desc')}
                </p>

                {/* Show error details in development */}
                {import.meta.env.DEV && error && (
                    <details className="text-left mb-6 bg-slate-100 p-4 rounded-lg border border-slate-200">
                        <summary className="cursor-pointer font-semibold text-sm text-slate-700 mb-2">
                            {t('error.tech_details')}
                        </summary>
                        <pre className="text-xs text-red-600 overflow-auto max-h-40">
                            {error.toString()}
                            {'\n\n'}
                            {error.stack}
                        </pre>
                    </details>
                )}

                <div className="flex flex-col gap-3">
                    <button
                        onClick={onReset}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onReset(); }}
                        className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all active:scale-[0.98]"
                    >
                        {t('error.reload_btn')}
                    </button>

                    <a
                        href="/"
                        className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-all active:scale-[0.98] block"
                    >
                        {t('error.go_home')}
                    </a>
                </div>

                <p className="text-xs text-slate-400 mt-6">
                    {t('error.support_hint')}
                </p>
            </div>
        </div>
    );
}

/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to console 
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Send to Sentry
        Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack
                }
            }
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null
        });

        // Reload the page as a last resort
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI using our translated functional component
            return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
        }

        return this.props.children;
    }
}
